import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Input, Button, Toggle } from '../design-system';
import { TimezoneInput } from '../design-system/TimezoneInput';
import { CalendarGoogleSync } from './CalendarGoogleSync';
import { DatePickerField } from '../DatePickerField';
import { TimePickerField } from '../TimePickerField';
import { colors, spacing, typography } from '../../theme';
import { useLocale } from '../../contexts/LocaleContext';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { useThemedStyles } from '../../hooks/useThemedStyles';

export interface CalendarFormData {
  name: string;
  description?: string;
  color?: string;
  timezone: string;
  isDefault?: boolean;
  isActive?: boolean;
  visibility?: 'private' | 'public' | 'shared';
  availability?: {
    mode: 'all_time' | 'specific_period' | 'customized_days';
    startDate?: Date;
    endDate?: Date;
    daysOfWeek?: number[]; // 0-6 for Sunday-Saturday
    isAllDay?: boolean; // If true, available all day; if false, use timeRange
    timeRange?: {
      startTime: string; // HH:MM format (e.g., "09:00")
      endTime: string; // HH:MM format (e.g., "17:00")
    };
    slotDurationMinutes?: number; // divides the availability window into bookable slots (e.g. 30)
    maxBookingsPerSlot?: number; // how many events can occupy the same slot before it's full (default 1)
  };
}

export interface CalendarFormProps {
  initialData?: Partial<CalendarFormData>;
  onSubmit: (data: CalendarFormData) => void;
  onCancel: () => void;
  submitLabel?: string;
  loading?: boolean;
  businessId?: string;
  userId?: string;
  hideActions?: boolean;
  submitRef?: React.MutableRefObject<(() => void) | null>;
  /** Existing calendar's id — only present in edit mode, since Google sync needs a real
   * calendar to map to (a not-yet-created calendar has nothing to link). */
  calendarId?: string;
  onNotify?: (type: 'success' | 'error', title: string, message: string) => void;
}

const CALENDAR_COLORS = [
  '#3B82F6', // Blue
  '#EF4444', // Red
  '#10B981', // Green
  '#F59E0B', // Amber
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#84CC16', // Lime
  '#F97316', // Orange
  '#6366F1', // Indigo
];

export const CalendarForm: React.FC<CalendarFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  submitLabel,
  loading = false,
  businessId,
  userId,
  hideActions = false,
  submitRef,
  calendarId,
  onNotify,
}) => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const resolvedSubmitLabel = submitLabel || t('calendar.calendarForm.saveButton');

  const [formData, setFormData] = useState<CalendarFormData>({
    name: initialData?.name || '',
    description: initialData?.description || '',
    color: initialData?.color || CALENDAR_COLORS[0],
    timezone: initialData?.timezone || 'UTC',
    isDefault: initialData?.isDefault || false,
    isActive: initialData?.isActive !== undefined ? initialData.isActive : true,
    visibility: initialData?.visibility || 'private',
    availability: initialData?.availability || {
      mode: 'all_time',
      startDate: undefined,
      endDate: undefined,
      daysOfWeek: [],
      isAllDay: true,
      timeRange: {
        startTime: '09:00',
        endTime: '17:00',
      },
      slotDurationMinutes: undefined,
      maxBookingsPerSlot: 1,
    },
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showColorPicker, setShowColorPicker] = useState(false);

  const DAYS_OF_WEEK = [
    { value: 0, label: 'Sunday', short: t('calendar.calendarForm.daySunShort') },
    { value: 1, label: 'Monday', short: t('calendar.calendarForm.dayMonShort') },
    { value: 2, label: 'Tuesday', short: t('calendar.calendarForm.dayTueShort') },
    { value: 3, label: 'Wednesday', short: t('calendar.calendarForm.dayWedShort') },
    { value: 4, label: 'Thursday', short: t('calendar.calendarForm.dayThuShort') },
    { value: 5, label: 'Friday', short: t('calendar.calendarForm.dayFriShort') },
    { value: 6, label: 'Saturday', short: t('calendar.calendarForm.daySatShort') },
  ];

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        color: initialData.color || CALENDAR_COLORS[0],
        timezone: initialData.timezone || 'UTC',
        isDefault: initialData.isDefault || false,
        isActive: initialData.isActive !== undefined ? initialData.isActive : true,
        visibility: initialData.visibility || 'private',
        availability: initialData.availability || {
          mode: 'all_time',
          startDate: undefined,
          endDate: undefined,
          daysOfWeek: [],
          isAllDay: true,
          timeRange: {
            startTime: '09:00',
            endTime: '17:00',
          },
          slotDurationMinutes: undefined,
          maxBookingsPerSlot: 1,
        },
      });
    }
  }, [initialData]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('calendar.calendarForm.nameRequiredError');
    } else if (formData.name.trim().length < 2) {
      newErrors.name = t('calendar.calendarForm.nameMinLengthError');
    }

    if (!formData.timezone) {
      newErrors.timezone = t('calendar.calendarForm.timezoneRequiredError');
    }

    // Validate availability settings
    if (formData.availability?.mode === 'specific_period') {
      if (!formData.availability.startDate) {
        newErrors['availability.startDate'] = t('calendar.calendarForm.startDateRequiredError');
      }
      if (!formData.availability.endDate) {
        newErrors['availability.endDate'] = t('calendar.calendarForm.endDateRequiredError');
      }
      if (
        formData.availability.startDate &&
        formData.availability.endDate &&
        formData.availability.endDate < formData.availability.startDate
      ) {
        newErrors['availability.endDate'] = t('calendar.calendarForm.endDateAfterStartError');
      }
    }

    if (formData.availability?.mode === 'customized_days') {
      if (!formData.availability.daysOfWeek || formData.availability.daysOfWeek.length === 0) {
        newErrors['availability.daysOfWeek'] = t('calendar.calendarForm.selectAtLeastOneDayError');
      }
    }

    // Validate time range if not all day
    if (
      formData.availability?.isAllDay === false &&
      formData.availability?.timeRange
    ) {
      if (!formData.availability.timeRange.startTime) {
        newErrors['availability.timeRange.startTime'] = t('calendar.calendarForm.startTimeRequiredError');
      }
      if (!formData.availability.timeRange.endTime) {
        newErrors['availability.timeRange.endTime'] = t('calendar.calendarForm.endTimeRequiredError');
      }
      if (
        formData.availability.timeRange.startTime &&
        formData.availability.timeRange.endTime &&
        formData.availability.timeRange.endTime <= formData.availability.timeRange.startTime
      ) {
        newErrors['availability.timeRange.endTime'] = t('calendar.calendarForm.endTimeAfterStartError');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onSubmit(formData);
    }
  };

  // Expose submit function via ref
  useEffect(() => {
    if (submitRef) {
      submitRef.current = handleSubmit;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const updateField = (field: keyof CalendarFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const updateAvailabilityField = (field: keyof CalendarFormData['availability'], value: any) => {
    setFormData((prev) => ({
      ...prev,
      availability: {
        ...prev.availability,
        [field]: value,
      } as any,
    }));
  };

  const toggleDayOfWeek = (day: number) => {
    const currentDays = formData.availability?.daysOfWeek || [];
    const newDays = currentDays.includes(day)
      ? currentDays.filter((d) => d !== day)
      : [...currentDays, day].sort();
    updateAvailabilityField('daysOfWeek', newDays);
  };

  const updateTimeRange = (field: 'startTime' | 'endTime', value: string) => {
    setFormData((prev) => ({
      ...prev,
      availability: {
        ...prev.availability,
        timeRange: {
          ...prev.availability?.timeRange,
          [field]: value,
        } as any,
      } as any,
    }));
  };

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.form}>
        {/* Name */}
        <View style={styles.field}>
          <Input
            label={t('calendar.calendarForm.nameLabel')}
            value={formData.name}
            onChangeText={(text) => updateField('name', text)}
            placeholder={t('calendar.calendarForm.namePlaceholder')}
            error={errors.name}
            autoCapitalize="words"
            maxLength={200}
          />
        </View>

        {/* Description */}
        <View style={styles.field}>
          <Input
            label={t('calendar.calendarForm.descriptionLabel')}
            value={formData.description || ''}
            onChangeText={(text) => updateField('description', text)}
            placeholder={t('calendar.calendarForm.descriptionPlaceholder')}
            multiline
            numberOfLines={3}
            maxLength={1000}
          />
        </View>

        {/* Color Picker */}
        <View style={styles.field}>
          <Text style={styles.label}>{t('calendar.calendarForm.colorLabel')}</Text>
          <TouchableOpacity
            style={[styles.colorPickerButton, { flexDirection: rtl.rowDirection }]}
            onPress={() => setShowColorPicker(!showColorPicker)}
          >
            <View style={[styles.colorPreview, { backgroundColor: formData.color }]} />
            <Text style={styles.colorText}>{formData.color}</Text>
            <Ionicons
              name={showColorPicker ? 'chevron-up' : 'chevron-down'}
              size={20}
              color={colors.textLight}
            />
          </TouchableOpacity>
          {showColorPicker && (
            <View style={[styles.colorGrid, { flexDirection: rtl.rowDirection }]}>
              {CALENDAR_COLORS.map((color) => (
                <TouchableOpacity
                  key={color}
                  style={[
                    styles.colorOption,
                    { backgroundColor: color },
                    formData.color === color && styles.colorOptionSelected,
                  ]}
                  onPress={() => {
                    updateField('color', color);
                    setShowColorPicker(false);
                  }}
                >
                  {formData.color === color && (
                    <Ionicons name="checkmark" size={16} color={colors.white} />
                  )}
                </TouchableOpacity>
              ))}
            </View>
          )}
        </View>

        {/* Timezone */}
        <View style={styles.field}>
          <TimezoneInput
            label={t('calendar.calendarForm.timezoneLabel')}
            value={formData.timezone}
            onChange={(timezone) => updateField('timezone', timezone)}
            error={errors.timezone}
            required
          />
        </View>

        {/* Visibility */}
        <View style={styles.field}>
          <Text style={styles.label}>{t('calendar.calendarForm.visibilityLabel')}</Text>
          <View style={[styles.inlineRadioGroup, { flexDirection: rtl.rowDirection }]}>
            <TouchableOpacity
              style={[
                styles.inlineRadioOption,
                { flexDirection: rtl.rowDirection },
                formData.visibility === 'private' && styles.inlineRadioOptionSelected,
              ]}
              onPress={() => updateField('visibility', 'private')}
            >
              <Ionicons
                name={formData.visibility === 'private' ? 'radio-button-on' : 'radio-button-off'}
                size={18}
                color={formData.visibility === 'private' ? colors.primary : colors.textLight}
              />
              <Ionicons name="lock-closed-outline" size={16} color={colors.text.primary} />
              <Text style={styles.inlineRadioText}>{t('calendar.calendarForm.visibilityPrivate')}</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.inlineRadioOption,
                { flexDirection: rtl.rowDirection },
                formData.visibility === 'public' && styles.inlineRadioOptionSelected,
              ]}
              onPress={() => updateField('visibility', 'public')}
            >
              <Ionicons
                name={formData.visibility === 'public' ? 'radio-button-on' : 'radio-button-off'}
                size={18}
                color={formData.visibility === 'public' ? colors.primary : colors.textLight}
              />
              <Ionicons name="globe-outline" size={16} color={colors.text.primary} />
              <Text style={styles.inlineRadioText}>{t('calendar.calendarForm.visibilityPublic')}</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.inlineRadioOption,
                { flexDirection: rtl.rowDirection },
                formData.visibility === 'shared' && styles.inlineRadioOptionSelected,
              ]}
              onPress={() => updateField('visibility', 'shared')}
            >
              <Ionicons
                name={formData.visibility === 'shared' ? 'radio-button-on' : 'radio-button-off'}
                size={18}
                color={formData.visibility === 'shared' ? colors.primary : colors.textLight}
              />
              <Ionicons name="people-outline" size={16} color={colors.text.primary} />
              <Text style={styles.inlineRadioText}>{t('calendar.calendarForm.visibilityShared')}</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Toggles */}
        <View style={styles.field}>
          <Toggle
            label={t('calendar.calendarForm.setDefaultLabel')}
            value={formData.isDefault || false}
            onValueChange={(value) => updateField('isDefault', value)}
            description={t('calendar.calendarForm.setDefaultDescription')}
          />
        </View>

        <View style={styles.field}>
          <Toggle
            label={t('calendar.calendarForm.activeLabel')}
            value={formData.isActive !== undefined ? formData.isActive : true}
            onValueChange={(value) => updateField('isActive', value)}
            description={t('calendar.calendarForm.activeDescription')}
          />
        </View>

        {/* Google Calendar Sync — only once the calendar exists (edit mode) */}
        {!!calendarId && (
          <CalendarGoogleSync
            calendarId={calendarId}
            scope={businessId ? 'business' : 'personal'}
            onNotify={onNotify || (() => {})}
          />
        )}

        {/* Availability Settings */}
        <View style={styles.field}>
          <Text style={styles.label}>{t('calendar.calendarForm.availabilityLabel')}</Text>
          <Text style={styles.helperText}>{t('calendar.calendarForm.availabilityHelperText')}</Text>

          {/* Availability Mode Selection */}
          <View style={[styles.inlineRadioGroup, { flexDirection: rtl.rowDirection }]}>
            <TouchableOpacity
              style={[
                styles.inlineRadioOption,
                { flexDirection: rtl.rowDirection },
                formData.availability?.mode === 'all_time' && styles.inlineRadioOptionSelected,
              ]}
              onPress={() => updateAvailabilityField('mode', 'all_time')}
            >
              <Ionicons
                name={formData.availability?.mode === 'all_time' ? 'radio-button-on' : 'radio-button-off'}
                size={18}
                color={formData.availability?.mode === 'all_time' ? colors.primary : colors.textLight}
              />
              <Ionicons name="time-outline" size={16} color={colors.text.primary} />
              <Text style={styles.inlineRadioText}>{t('calendar.calendarForm.availabilityModeAllTime')}</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.inlineRadioOption,
                { flexDirection: rtl.rowDirection },
                formData.availability?.mode === 'specific_period' && styles.inlineRadioOptionSelected,
              ]}
              onPress={() => updateAvailabilityField('mode', 'specific_period')}
            >
              <Ionicons
                name={formData.availability?.mode === 'specific_period' ? 'radio-button-on' : 'radio-button-off'}
                size={18}
                color={formData.availability?.mode === 'specific_period' ? colors.primary : colors.textLight}
              />
              <Ionicons name="calendar-outline" size={16} color={colors.text.primary} />
              <Text style={styles.inlineRadioText}>{t('calendar.calendarForm.availabilityModePeriod')}</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.inlineRadioOption,
                { flexDirection: rtl.rowDirection },
                formData.availability?.mode === 'customized_days' && styles.inlineRadioOptionSelected,
              ]}
              onPress={() => updateAvailabilityField('mode', 'customized_days')}
            >
              <Ionicons
                name={formData.availability?.mode === 'customized_days' ? 'radio-button-on' : 'radio-button-off'}
                size={18}
                color={formData.availability?.mode === 'customized_days' ? colors.primary : colors.textLight}
              />
              <Ionicons name="calendar-number-outline" size={16} color={colors.text.primary} />
              <Text style={styles.inlineRadioText}>{t('calendar.calendarForm.availabilityModeDays')}</Text>
            </TouchableOpacity>
          </View>

          {/* Specific Period Fields */}
          {formData.availability?.mode === 'specific_period' && (
            <View style={styles.availabilityFields}>
              <View style={[styles.inlineDateFields, { flexDirection: rtl.rowDirection }]}>
                <View style={[styles.dateField, styles.inlineDateField, rtl.marginEnd(spacing.md)]}>
                  <DatePickerField
                    label={t('calendar.calendarForm.startDateLabel')}
                    value={formData.availability?.startDate ?? null}
                    onChange={(date) => updateAvailabilityField('startDate', date ?? undefined)}
                  />
                </View>

                <View style={[styles.dateField, styles.inlineDateField, rtl.marginEnd(0)]}>
                  <DatePickerField
                    label={t('calendar.calendarForm.endDateLabel')}
                    value={formData.availability?.endDate ?? null}
                    onChange={(date) => updateAvailabilityField('endDate', date ?? undefined)}
                    minimumDate={formData.availability?.startDate}
                  />
                </View>
              </View>

              {/* Time Range for Specific Period */}
              <View style={styles.field}>
                <Toggle
                  label={t('calendar.calendarForm.allDayLabel')}
                  value={formData.availability?.isAllDay !== undefined ? formData.availability.isAllDay : true}
                  onValueChange={(value) => updateAvailabilityField('isAllDay', value)}
                  description={t('calendar.calendarForm.allDayDescription')}
                />
              </View>

              {formData.availability?.isAllDay === false && (
                <View style={[styles.inlineTimeFields, { flexDirection: rtl.rowDirection }]}>
                  <View style={[styles.timeField, styles.inlineTimeField, rtl.marginEnd(spacing.md)]}>
                    <TimePickerField
                      label={t('calendar.calendarForm.startTimeLabel')}
                      value={formData.availability?.timeRange?.startTime || '09:00'}
                      onChange={(time) => updateTimeRange('startTime', time)}
                    />
                  </View>

                  <View style={[styles.timeField, styles.inlineTimeField, rtl.marginEnd(0)]}>
                    <TimePickerField
                      label={t('calendar.calendarForm.endTimeLabel')}
                      value={formData.availability?.timeRange?.endTime || '17:00'}
                      onChange={(time) => updateTimeRange('endTime', time)}
                    />
                  </View>
                </View>
              )}
            </View>
          )}

          {/* Customized Days Fields */}
          {formData.availability?.mode === 'customized_days' && (
            <View style={styles.availabilityFields}>
              <Text style={styles.daysLabel}>{t('calendar.calendarForm.selectDaysOfWeekLabel')}</Text>
              <View style={[styles.daysGrid, { flexDirection: rtl.rowDirection }]}>
                {DAYS_OF_WEEK.map((day) => {
                  const isSelected = formData.availability?.daysOfWeek?.includes(day.value) || false;
                  return (
                    <TouchableOpacity
                      key={day.value}
                      style={[
                        styles.dayButton,
                        isSelected && styles.dayButtonSelected,
                      ]}
                      onPress={() => toggleDayOfWeek(day.value)}
                    >
                      <Text style={[styles.dayText, isSelected && styles.dayTextSelected]}>
                        {day.short}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
              {formData.availability?.daysOfWeek && formData.availability.daysOfWeek.length === 0 && (
                <Text style={styles.errorText}>{t('calendar.calendarForm.selectAtLeastOneDayError')}</Text>
              )}

              {/* Time Range for Customized Days */}
              <View style={styles.field}>
                <Toggle
                  label={t('calendar.calendarForm.allDayLabel')}
                  value={formData.availability?.isAllDay !== undefined ? formData.availability.isAllDay : true}
                  onValueChange={(value) => updateAvailabilityField('isAllDay', value)}
                  description={t('calendar.calendarForm.allDayDescription')}
                />
              </View>

              {formData.availability?.isAllDay === false && (
                <View style={[styles.inlineTimeFields, { flexDirection: rtl.rowDirection }]}>
                  <View style={[styles.timeField, styles.inlineTimeField, rtl.marginEnd(spacing.md)]}>
                    <TimePickerField
                      label={t('calendar.calendarForm.startTimeLabel')}
                      value={formData.availability?.timeRange?.startTime || '09:00'}
                      onChange={(time) => updateTimeRange('startTime', time)}
                    />
                  </View>

                  <View style={[styles.timeField, styles.inlineTimeField, rtl.marginEnd(0)]}>
                    <TimePickerField
                      label={t('calendar.calendarForm.endTimeLabel')}
                      value={formData.availability?.timeRange?.endTime || '17:00'}
                      onChange={(time) => updateTimeRange('endTime', time)}
                    />
                  </View>
                </View>
              )}
            </View>
          )}

          {/* Slot Duration + Capacity — used by flow booking nodes to compute live available
              time slots from this calendar's availability window. */}
          <View style={[styles.inlineDateFields, { flexDirection: rtl.rowDirection }]}>
            <View style={[styles.dateField, styles.inlineDateField, rtl.marginEnd(spacing.md)]}>
              <Input
                label={t('calendar.calendarForm.slotDurationLabel')}
                value={formData.availability?.slotDurationMinutes ? String(formData.availability.slotDurationMinutes) : ''}
                onChangeText={(text) => updateAvailabilityField('slotDurationMinutes', text ? Number(text.replace(/[^0-9]/g, '')) : undefined)}
                placeholder={t('calendar.calendarForm.slotDurationPlaceholder')}
                keyboardType="numeric"
              />
            </View>
            <View style={[styles.dateField, styles.inlineDateField, rtl.marginEnd(0)]}>
              <Input
                label={t('calendar.calendarForm.maxBookingsPerSlotLabel')}
                value={formData.availability?.maxBookingsPerSlot !== undefined ? String(formData.availability.maxBookingsPerSlot) : '1'}
                onChangeText={(text) => updateAvailabilityField('maxBookingsPerSlot', text ? Number(text.replace(/[^0-9]/g, '')) : 1)}
                placeholder={t('calendar.calendarForm.maxBookingsPerSlotPlaceholder')}
                keyboardType="numeric"
              />
            </View>
          </View>
          <Text style={styles.helperText}>{t('calendar.calendarForm.slotDurationHelperText')}</Text>
        </View>

        {/* Action Buttons */}
        {!hideActions && (
          <View style={[styles.actions, { flexDirection: rtl.rowDirection }]}>
            <Button
              label={t('calendar.calendarForm.cancelButton')}
              onPress={onCancel}
              variant="outline"
              style={styles.cancelButton}
              disabled={loading}
            />
            <Button
              label={resolvedSubmitLabel}
              onPress={handleSubmit}
              loading={loading}
              style={styles.submitButton}
            />
          </View>
        )}
      </View>
    </ScrollView>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    padding: spacing.md,
  },
  form: {
    gap: spacing.md,
  },
  field: {
    marginBottom: spacing.md,
  },
  label: {
    ...typography.body,
    fontWeight: '600',
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
  colorPickerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    backgroundColor: colors.background.primary,
    gap: spacing.sm,
  },
  colorPreview: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border,
  },
  colorText: {
    ...typography.body,
    color: colors.text.primary,
    flex: 1,
  },
  colorGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginTop: spacing.sm,
    padding: spacing.sm,
    backgroundColor: colors.background.primary,
    borderRadius: 8,
  },
  colorOption: {
    width: 40,
    height: 40,
    borderRadius: 20,
    borderWidth: 2,
    borderColor: 'transparent',
    justifyContent: 'center',
    alignItems: 'center',
  },
  colorOptionSelected: {
    borderColor: colors.primary,
  },
  radioGroup: {
    gap: spacing.sm,
  },
  radioOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    backgroundColor: colors.background.primary,
    gap: spacing.sm,
  },
  radioOptionSelected: {
    borderColor: colors.primary,
    backgroundColor: colors.primaryLight,
  },
  radioText: {
    ...typography.body,
    color: colors.text.primary,
    flex: 1,
  },
  inlineRadioGroup: {
    flexDirection: 'row',
    gap: spacing.sm,
    flexWrap: 'wrap',
  },
  inlineRadioOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    backgroundColor: colors.background.primary,
    gap: spacing.xs,
    flex: 1,
    minWidth: 100,
  },
  inlineRadioOptionSelected: {
    borderColor: colors.primary,
    backgroundColor: colors.primaryLight,
  },
  inlineRadioText: {
    ...typography.caption,
    color: colors.text.primary,
    fontSize: typography.fontSize.sm,
  },
  actions: {
    flexDirection: 'row',
    gap: spacing.md,
    marginTop: spacing.lg,
    marginBottom: spacing.xl,
  },
  cancelButton: {
    flex: 1,
  },
  submitButton: {
    flex: 1,
  },
  helperText: {
    ...typography.caption,
    color: colors.textLight,
    marginTop: spacing.xs,
    marginBottom: spacing.sm,
  },
  availabilityFields: {
    marginTop: spacing.md,
    gap: spacing.md,
  },
  inlineDateFields: {
    flexDirection: 'row',
    gap: spacing.md,
  },
  dateField: {
    marginBottom: spacing.md,
  },
  inlineDateField: {
    flex: 1,
    marginBottom: 0,
  },
  dateLabel: {
    ...typography.body,
    fontWeight: '600',
    color: colors.text.primary,
    marginBottom: spacing.xs,
    fontSize: typography.fontSize.sm,
  },
  daysLabel: {
    ...typography.body,
    fontWeight: '600',
    color: colors.text.primary,
    marginBottom: spacing.sm,
  },
  daysGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  dayButton: {
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    backgroundColor: colors.background.primary,
    minWidth: 60,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dayButtonSelected: {
    borderColor: colors.primary,
    backgroundColor: colors.primaryLight,
  },
  dayText: {
    ...typography.body,
    color: colors.text.primary,
    fontWeight: '500',
  },
  dayTextSelected: {
    color: colors.primary,
    fontWeight: '600',
  },
  errorText: {
    ...typography.caption,
    color: colors.error,
    marginTop: spacing.xs,
  },
  timeRangeContainer: {
    marginTop: spacing.md,
    gap: spacing.md,
  },
  inlineTimeFields: {
    flexDirection: 'row',
    gap: spacing.md,
  },
  timeField: {
    marginBottom: spacing.md,
  },
  inlineTimeField: {
    flex: 1,
    marginBottom: 0,
  },
  timeLabel: {
    ...typography.body,
    fontWeight: '600',
    color: colors.text.primary,
    marginBottom: spacing.xs,
    fontSize: typography.fontSize.sm,
  },
});

